import React from 'react';

import { StatusBar as ExpoStatusBar } from 'expo-status-bar';
import { StatusBar as RNStatusBar, SafeAreaView, ScrollView, StyleSheet, View } from 'react-native';

import { modalHeaderStyles } from 'components/card-modal-header';
import { OrderProcessingLoading } from 'components/order-processing-loading';
import { Header } from 'components/ui/header';
import Totals from 'experiments/checkout-v2/treatment/features/checkout/components/totals';
import { AddPaymentMethodModal } from 'experiments/checkout-v2/treatment/features/payment/components/add-payment-method-modal';
import { AddReloadPrepaidMethodModal } from 'experiments/checkout-v2/treatment/features/payment/components/add-reload-prepaid-method-modal';
import { useExposeOrderPendingProcessModalExperiment } from 'experiments/use-order-pending-process-modal-experiment';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { isAndroid } from 'utils/environment';

import { CheckoutProvider } from '../checkout-provider';
import { PaymentSelector } from '../components';
import { CheckoutErrorsModal } from '../components/checkout-errors-modal';
import { CheckoutFooter } from '../components/checkout-footer';
import { InternalPriceMismatchDiagnostic } from '../components/internal-price-mismatch-diagnostic';
import { OrderPriceChangeToast } from '../components/order-price-change-toast';
import { ServiceModeWidget } from '../components/service-mode';
import { StoreLocatorWithMapWidget } from '../components/store-locator-with-map';
import { TipSelector } from '../components/tip-selector';
import { useCheckoutBackNavigationGuard } from '../hooks/use-checkout-back-navigation-guard';
import { useCheckoutContext } from '../hooks/use-checkout-context';
import { useCheckoutInit } from '../hooks/use-checkout-init';
import { useCheckoutStoreChangeReprice } from '../hooks/use-checkout-store-change-reprice';

const CheckoutHeader = ({ onBackPress }: { onBackPress: () => void }) => {
  const headerTopPadding = isAndroid
    ? RNStatusBar.currentHeight ?? modalHeaderStyles.headerContainer.padding
    : 0;

  return (
    <View style={{ paddingTop: headerTopPadding, backgroundColor: primitive.$bbqBrown }}>
      <Header
        title={t('checkout')}
        closeIconVariant="back"
        closeIconPosition="left"
        onCloseIconClick={onBackPress}
      />
    </View>
  );
};

/**
 * It is used to display all the widgets for the checkout page.
 */
const CheckoutPage = () => {
  useExposeOrderPendingProcessModalExperiment();
  useCheckoutInit();
  useCheckoutStoreChangeReprice();
  const { handleBackPress, showRecoveryLoading } = useCheckoutBackNavigationGuard();
  const { serverOrder, isDeliveryOrder } = useCheckoutContext();

  return (
    <>
      {/* eslint-disable react/style-prop-object */}
      <ExpoStatusBar style={'light'} translucent={isAndroid} backgroundColor="transparent" />
      <SafeAreaView style={{ flex: 0, backgroundColor: primitive.$bbqBrown }} />
      <View style={styles.container}>
        <CheckoutHeader onBackPress={handleBackPress} />
        <ScrollView>
          <View style={styles.scroller}>
            <InternalPriceMismatchDiagnostic />
            <StoreLocatorWithMapWidget />
            <PaymentSelector />
            {!isDeliveryOrder && <ServiceModeWidget />}
            {isDeliveryOrder && <TipSelector />}
            {/* note that this one is 100% cart dependent  */}
            <Totals />
            {/* TODO: treat this one as global modal, should be moved to appModals */}
            <AddPaymentMethodModal />
            <AddReloadPrepaidMethodModal />
          </View>
        </ScrollView>
        <CheckoutFooter />
        <CheckoutErrorsModal />
      </View>
      {!isDeliveryOrder && <OrderPriceChangeToast serverOrder={serverOrder} />}
      {showRecoveryLoading ? (
        <OrderProcessingLoading testID="checkout-order-submission-loading" />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: primitive.$mayoEggWhite,
  },
  scroller: {
    flex: 1,
    paddingHorizontal: spacing.$4,
    gap: spacing.$4,
    paddingVertical: spacing.$4,
    width: '100%',
    maxWidth: 560,
    margin: 'auto',
  },
});

const ProvidedCheckout = () => (
  <CheckoutProvider>
    <CheckoutPage />
  </CheckoutProvider>
);

export default ProvidedCheckout;
